/**
 * Athena Event Bus
 *
 * A pequena fronteira local entre sinais do dispositivo e a interface. Ela
 * nunca transporta texto de conversa, transcrições, conteúdo de notificações,
 * URLs externas, arquivos ou identificadores de conta/dispositivo. Cada fonte
 * precisa reduzir o sinal a metadados permitidos antes de publicá-lo.
 */

export const ATHENA_EVENT_TYPES = Object.freeze({
  APP_FOREGROUND: 'APP_FOREGROUND',
  APP_BACKGROUND: 'APP_BACKGROUND',
  NETWORK_CHANGED: 'NETWORK_CHANGED',
  NOTIFICATION_RECEIVED: 'NOTIFICATION_RECEIVED',
  NOTIFICATION_ACTION: 'NOTIFICATION_ACTION',
  PUSH_TOKEN_REFRESH: 'PUSH_TOKEN_REFRESH',
  ALARM_EVENT: 'ALARM_EVENT',
  SHARE_RECEIVED: 'SHARE_RECEIVED',
  DEVICE_CONTEXT_CHANGED: 'DEVICE_CONTEXT_CHANGED',
  NATIVE_INTENT_RECEIVED: 'NATIVE_INTENT_RECEIVED',
  SHORTCUT_USED: 'SHORTCUT_USED',
  WIDGET_ACTION: 'WIDGET_ACTION',
  TILE_ACTION: 'TILE_ACTION',
  QUICK_CAPTURE_SUBMITTED: 'QUICK_CAPTURE_SUBMITTED',
  AUTOMATION_TRIGGERED: 'AUTOMATION_TRIGGERED',
  OTA_CHECK: 'OTA_CHECK',
});

export const ATHENA_EVENT_SOURCES = Object.freeze({
  APP: 'app',
  NETWORK: 'network',
  NOTIFICATION: 'notification',
  ALARM: 'alarm',
  SHARE: 'share',
  DEVICE: 'device',
  INTENT: 'intent',
  SHORTCUT: 'shortcut',
  WIDGET: 'widget',
  TILE: 'tile',
  QUICK_CAPTURE: 'quick_capture',
  AUTOMATION: 'automation',
  UPDATE: 'update',
});

const EVENT_TYPES = new Set(Object.values(ATHENA_EVENT_TYPES));
const EVENT_SOURCES = new Set(Object.values(ATHENA_EVENT_SOURCES));
const MAX_DEPTH = 3;
const MAX_DEDUPLICATION_ENTRIES = 240;
const DEDUPLICATION_WINDOW_MS = 2 * 60 * 1000;
const listeners = new Set();
const recentIdempotencyKeys = new Map();
const diagnostics = [];
const MAX_DIAGNOSTICS = 120;

const CONNECTION_TYPES = new Set(['wifi', 'cellular', 'none', 'unknown', 'ethernet', 'bluetooth']);
const NOTIFICATION_TYPES = new Set(['reminder', 'alarm', 'proactive', 'task', 'calendar', 'system', 'follow_up', 'test']);
const NOTIFICATION_ACTIONS = new Set(['open', 'snooze', 'dismiss', 'none']);
const INTENT_TYPES = new Set([
  'OPEN_HOME', 'OPEN_QUICK', 'OPEN_CHAT', 'OPEN_PROJECT', 'OPEN_CALENDAR', 'OPEN_TASK', 'OPEN_WORKSPACE',
  'START_VOICE', 'NEW_TASK', 'NEW_REMINDER', 'CAPTURE_IDEA', 'IMPORT_CONTENT',
]);
const QUICK_CAPTURE_KINDS = new Set(['inbox', 'open_loop', 'task']);
const DEVICE_SIGNAL_TYPES = new Set(['snapshot', 'battery', 'network', 'audio', 'app_state', 'capabilities']);

function nowIso() {
  return new Date().toISOString();
}

function identifier(value, maximum = 120) {
  if (typeof value !== 'string') return '';
  const cleaned = value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, maximum);
  return /^[A-Za-z0-9._:-]+$/.test(cleaned) ? cleaned : '';
}

function enumValue(value, allowed, fallback = '') {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return allowed.has(normalized) ? normalized : fallback;
}

function safeBoolean(value) {
  return typeof value === 'boolean' ? value : undefined;
}

function safeInteger(value, minimum, maximum) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= minimum && numeric <= maximum ? numeric : undefined;
}

function uuid() {
  try {
    return `evt-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
  } catch {
    return `evt-${Math.random().toString(36).slice(2)}`;
  }
}

function metadataFor(type, raw) {
  const input = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const metadata = {};

  switch (type) {
    case ATHENA_EVENT_TYPES.APP_FOREGROUND:
    case ATHENA_EVENT_TYPES.APP_BACKGROUND: {
      const active = safeBoolean(input.active);
      if (active !== undefined) metadata.active = active;
      break;
    }
    case ATHENA_EVENT_TYPES.NETWORK_CHANGED: {
      const connected = safeBoolean(input.connected);
      const connectionType = enumValue(input.connectionType, CONNECTION_TYPES, 'unknown');
      if (connected !== undefined) metadata.connected = connected;
      metadata.connectionType = connectionType;
      break;
    }
    case ATHENA_EVENT_TYPES.NOTIFICATION_RECEIVED:
    case ATHENA_EVENT_TYPES.NOTIFICATION_ACTION:
    case ATHENA_EVENT_TYPES.ALARM_EVENT: {
      const notificationId = identifier(input.notificationId, 120);
      const typeValue = enumValue(input.notificationType || input.type, NOTIFICATION_TYPES, 'system');
      const action = enumValue(input.action, NOTIFICATION_ACTIONS, 'none');
      const canOpen = safeBoolean(input.canOpen);
      if (notificationId) metadata.notificationId = notificationId;
      metadata.notificationType = typeValue;
      metadata.action = action;
      if (canOpen !== undefined) metadata.canOpen = canOpen;
      break;
    }
    case ATHENA_EVENT_TYPES.PUSH_TOKEN_REFRESH:
      // Deliberately no token or device identifier.
      metadata.refreshed = true;
      break;
    case ATHENA_EVENT_TYPES.SHARE_RECEIVED: {
      const attachmentCount = safeInteger(input.attachmentCount, 0, 20);
      const hasText = safeBoolean(input.hasText);
      if (attachmentCount !== undefined) metadata.attachmentCount = attachmentCount;
      if (hasText !== undefined) metadata.hasText = hasText;
      break;
    }
    case ATHENA_EVENT_TYPES.DEVICE_CONTEXT_CHANGED: {
      const signal = enumValue(input.signal, DEVICE_SIGNAL_TYPES, 'snapshot');
      const available = safeBoolean(input.available);
      metadata.signal = signal;
      if (available !== undefined) metadata.available = available;
      break;
    }
    case ATHENA_EVENT_TYPES.NATIVE_INTENT_RECEIVED:
    case ATHENA_EVENT_TYPES.SHORTCUT_USED:
    case ATHENA_EVENT_TYPES.WIDGET_ACTION:
    case ATHENA_EVENT_TYPES.TILE_ACTION: {
      const intent = typeof input.intent === 'string' ? input.intent.trim().toUpperCase() : '';
      if (!INTENT_TYPES.has(intent)) return null;
      metadata.intent = intent;
      break;
    }
    case ATHENA_EVENT_TYPES.QUICK_CAPTURE_SUBMITTED: {
      const kind = enumValue(input.kind, QUICK_CAPTURE_KINDS);
      if (!kind) return null;
      metadata.kind = kind;
      break;
    }
    case ATHENA_EVENT_TYPES.AUTOMATION_TRIGGERED: {
      const automationId = identifier(input.automationId, 120);
      const trigger = identifier(input.trigger, 80);
      if (automationId) metadata.automationId = automationId;
      if (trigger) metadata.trigger = trigger;
      break;
    }
    case ATHENA_EVENT_TYPES.OTA_CHECK: {
      const channel = enumValue(input.channel, new Set(['development', 'preview', 'beta', 'stable']), 'stable');
      metadata.channel = channel;
      break;
    }
    default:
      return null;
  }

  return Object.freeze(metadata);
}

function pruneDeduplication(now = Date.now()) {
  for (const [key, timestamp] of recentIdempotencyKeys) {
    if (now - timestamp > DEDUPLICATION_WINDOW_MS) recentIdempotencyKeys.delete(key);
  }
  while (recentIdempotencyKeys.size > MAX_DEDUPLICATION_ENTRIES) {
    recentIdempotencyKeys.delete(recentIdempotencyKeys.keys().next().value);
  }
}

function rememberDiagnostic(event) {
  diagnostics.push(Object.freeze({
    id: event.id,
    type: event.type,
    source: event.source,
    timestamp: event.timestamp,
    depth: event.depth,
  }));
  if (diagnostics.length > MAX_DIAGNOSTICS) diagnostics.splice(0, diagnostics.length - MAX_DIAGNOSTICS);
}

/**
 * Emits a closed, content-free event. Unknown types/sources and invalid
 * metadata are ignored rather than coerced into executable behaviour.
 */
export function emitAthenaEvent({
  type,
  source,
  metadata = {},
  idempotencyKey = '',
  origin = '',
  depth = 0,
} = {}) {
  if (!EVENT_TYPES.has(type) || !EVENT_SOURCES.has(source)) return null;
  const safeDepth = safeInteger(depth, 0, MAX_DEPTH);
  if (safeDepth === undefined) return null;
  const safeMetadata = metadataFor(type, metadata);
  if (!safeMetadata) return null;

  const safeIdempotencyKey = identifier(idempotencyKey, 180);
  const timestampNow = Date.now();
  pruneDeduplication(timestampNow);
  if (safeIdempotencyKey) {
    const previous = recentIdempotencyKeys.get(safeIdempotencyKey);
    if (previous && timestampNow - previous <= DEDUPLICATION_WINDOW_MS) return null;
    recentIdempotencyKeys.set(safeIdempotencyKey, timestampNow);
  }

  const event = Object.freeze({
    id: uuid(),
    type,
    source,
    timestamp: nowIso(),
    depth: safeDepth,
    origin: identifier(origin, 80) || source,
    idempotencyKey: safeIdempotencyKey || null,
    metadata: safeMetadata,
  });

  rememberDiagnostic(event);
  listeners.forEach((listener) => {
    try { listener(event); } catch { /* Event consumers are isolated. */ }
  });
  return event;
}

export function subscribeAthenaEvents(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAthenaEventDiagnostics({ limit = 60 } = {}) {
  const safeLimit = Number.isInteger(limit) ? Math.max(0, Math.min(limit, MAX_DIAGNOSTICS)) : 60;
  return diagnostics.slice(-safeLimit).map((event) => ({ ...event }));
}

export function clearAthenaEventBusDiagnostics() {
  diagnostics.splice(0, diagnostics.length);
  recentIdempotencyKeys.clear();
}
